/* MealComplianceWidget — Personalized Meal Intelligence AI.
 *
 * Features:
 *  1. Live 0-100 compliance score за въведена храна срещу ВСИЧКИ
 *     assigned phenotypes (POST /api/pathway-score/meal-compliance).
 *  2. "Предложи следващо хранене" (top-5 suggestions) на база
 *     днешните coverage gaps (POST /api/pathway-score/next-meal-suggestion).
 *
 * Визуализация:
 *  - Input + live badge (🟢/🟡/🟠/🔴) + rationale
 *  - Matched emphasise / avoid / contraindication списъци
 *  - Conflicts warning (когато avoid и emphasise се сблъскат)
 *  - Модален списък с top-5 next meal предложения
 */
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Brain, CheckCircle2, AlertTriangle, XCircle, Lightbulb,
  ArrowRight, Loader2, Search, Plus, X,
} from "lucide-react";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";

const STATUS_STYLE = {
  excellent: { color: "#10B981", icon: CheckCircle2, bg_dark: "bg-emerald-500/20", bg_light: "bg-emerald-100", text_dark: "text-emerald-300", text_light: "text-emerald-700" },
  good:      { color: "#22C55E", icon: CheckCircle2, bg_dark: "bg-green-500/20",   bg_light: "bg-green-100",   text_dark: "text-green-300",   text_light: "text-green-700" },
  caution:   { color: "#F59E0B", icon: AlertTriangle, bg_dark: "bg-amber-500/20",   bg_light: "bg-amber-100",   text_dark: "text-amber-300",   text_light: "text-amber-700" },
  avoid:     { color: "#EF4444", icon: XCircle,       bg_dark: "bg-red-500/20",     bg_light: "bg-red-100",     text_dark: "text-red-300",     text_light: "text-red-700" },
};

function ScoreBadge({ score, status, isDark }) {
  const style = STATUS_STYLE[status] || STATUS_STYLE.caution;
  const Icon = style.icon;
  return (
    <div
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold
        ${isDark ? style.bg_dark + " " + style.text_dark : style.bg_light + " " + style.text_light}`}
      data-testid="compliance-score-badge"
    >
      <Icon className="w-3.5 h-3.5" />
      <span>{score}/100</span>
    </div>
  );
}

function NextMealModal({ open, onClose, API, isDark, onAddMeal }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(null);

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.post(`${API}/pathway-score/next-meal-suggestion`, { top_n: 5 });
        setData(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Грешка при зареждане на предложения");
      } finally {
        setLoading(false);
      }
    })();
  }, [open, API]);

  if (!open) return null;

  const handleAdd = async (sug, idx) => {
    setAdding(idx);
    try {
      await onAddMeal?.(sug);
      toast.success(`Добавено: ${sug.name_bg}`);
    } catch (e) {
      toast.error("Грешка при добавяне");
    } finally {
      setAdding(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
      data-testid="next-meal-modal"
    >
      <div
        className={`w-full max-w-lg max-h-[85vh] overflow-hidden rounded-xl shadow-2xl
          ${isDark ? "bg-gray-900 border border-white/10" : "bg-white"}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`flex items-center justify-between p-4 border-b ${isDark ? "border-white/10" : "border-gray-200"}`}>
          <div className="flex items-center gap-2">
            <Lightbulb className={`w-5 h-5 ${isDark ? "text-amber-300" : "text-amber-600"}`} />
            <h3 className={`text-sm font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Топ 5 умни предложения за следващо хранене
            </h3>
          </div>
          <button onClick={onClose} className={`p-1 rounded hover:bg-black/10 ${isDark ? "text-gray-400" : "text-gray-500"}`}
            data-testid="next-meal-modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 overflow-y-auto max-h-[calc(85vh-8rem)] space-y-2">
          {loading && (
            <div className={`flex items-center gap-2 text-xs ${isDark ? "text-gray-400" : "text-gray-600"}`}>
              <Loader2 className="w-4 h-4 animate-spin" />
              Анализирам вашите coverage gaps...
            </div>
          )}
          {!loading && data && (
            <>
              <div className={`text-[10px] uppercase tracking-wider font-semibold ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                Базирано на {data.assigned_phenotypes?.length || 0} фенотип(а) · {data.gaps?.length || 0} непокрити интервенции
              </div>
              {(!data.suggestions || data.suggestions.length === 0) && (
                <div className={`text-xs italic ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  Няма налични предложения — всички интервенции вече са покрити.
                </div>
              )}
              {(data.suggestions || []).map((sug, idx) => (
                <div key={`${sug.name_bg || "sug"}-${idx}`}
                  className={`rounded-lg p-3 border ${isDark ? "bg-white/5 border-white/10" : "bg-gray-50 border-gray-200"}`}
                  data-testid={`next-meal-suggestion-${idx}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
                        {sug.name_bg}
                      </div>
                      {sug.rationale_bg && (
                        <div className={`text-[11px] mt-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                          {sug.rationale_bg}
                        </div>
                      )}
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold
                          ${sug.evidence_level === "A"
                            ? (isDark ? "bg-emerald-500/20 text-emerald-300" : "bg-emerald-100 text-emerald-700")
                            : sug.evidence_level === "B"
                            ? (isDark ? "bg-amber-500/20 text-amber-300" : "bg-amber-100 text-amber-700")
                            : (isDark ? "bg-gray-500/20 text-gray-300" : "bg-gray-100 text-gray-600")
                          }`}>
                          Evidence {sug.evidence_level}
                        </span>
                        {sug.cross_phenotype_count > 1 && (
                          <span className={`text-[10px] ${isDark ? "text-indigo-300" : "text-indigo-600"}`}>
                            ✓ Покрива {sug.cross_phenotype_count} фенотипа
                          </span>
                        )}
                        {sug.calories > 0 && (
                          <span className={`text-[10px] ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                            {sug.calories} kcal
                          </span>
                        )}
                      </div>
                      <div className={`text-[10px] mt-1 ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                        → {sug.targets_intervention_bg}
                      </div>
                    </div>
                    {onAddMeal && (
                      <button
                        onClick={() => handleAdd(sug, idx)}
                        disabled={adding === idx}
                        className={`shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-semibold transition-colors
                          ${isDark ? "bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30" : "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"}
                          disabled:opacity-50`}
                        data-testid={`next-meal-add-${idx}`}
                      >
                        {adding === idx ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                        Лог
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function MealComplianceWidget({ refreshKey = 0, onMealAdded }) {
  const { API, language } = useAuth();
  const { isDark } = useTheme();
  const bg = language === "bg";

  const [assignedCount, setAssignedCount] = useState(0);
  const [foodName, setFoodName] = useState("");
  const [result, setResult] = useState(null);
  const [checking, setChecking] = useState(false);
  const [showNextMeal, setShowNextMeal] = useState(false);

  // Check how many phenotypes are assigned — показваме widget-а само ако ≥1
  const fetchAssigned = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/phenotype-subtype/my`);
      const count = Object.keys(data?.subtypes || {}).length;
      setAssignedCount(count);
    } catch {
      setAssignedCount(0);
    }
  }, [API]);

  useEffect(() => { fetchAssigned(); }, [fetchAssigned, refreshKey]);

  const handleCheck = async () => {
    const name = foodName.trim();
    if (!name) {
      toast.error("Въведете име на храна");
      return;
    }
    setChecking(true);
    try {
      const { data } = await axios.post(`${API}/pathway-score/meal-compliance`, { food_name: name });
      setResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Грешка");
      setResult(null);
    } finally {
      setChecking(false);
    }
  };

  const handleAddSuggestion = async (sug) => {
    // Добавя предложението като meal_log запис (lunch по default)
    await axios.post(`${API}/meals/add`, {
      food_name: sug.name_bg,
      amount_g: sug.portion_g || 100,
      calories: sug.calories || 0,
      meal_type: "lunch",
    });
    onMealAdded?.();
    window.dispatchEvent(new Event("meal-added"));
  };

  if (assignedCount === 0) return null;

  return (
    <>
      <div
        className={`rounded-lg overflow-hidden border ${isDark ? "border-indigo-500/20 bg-indigo-500/5" : "border-indigo-200 bg-indigo-50/40"}`}
        data-testid="meal-compliance-widget"
      >
        <div className="p-3 sm:p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${isDark ? "bg-indigo-500/20" : "bg-indigo-100"}`}>
              <Brain className={`w-4 h-4 ${isDark ? "text-indigo-300" : "text-indigo-600"}`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className={`text-[10px] uppercase tracking-wider font-semibold ${isDark ? "text-indigo-300" : "text-indigo-700"}`}>
                {bg ? "Препоръки по фенотипни пътища" : "Phenotype Pathway Picks"}
              </div>
              <div className={`text-xs ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                {bg
                  ? `Общи препоръки за вашите ${assignedCount} клинични фенотипа`
                  : `Cross-pathway food advice for your ${assignedCount} clinical phenotype${assignedCount === 1 ? "" : "s"}`}
              </div>
            </div>
            <button
              onClick={() => setShowNextMeal(true)}
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors
                ${isDark ? "bg-amber-500/20 text-amber-300 hover:bg-amber-500/30" : "bg-amber-100 text-amber-700 hover:bg-amber-200"}`}
              data-testid="next-meal-suggest-btn"
              title={bg ? "Топ 5 предложения, които покриват най-много фенотипни пътища" : "Top-5 picks that cover the most phenotype pathways"}
            >
              <Lightbulb className="w-3 h-3" />
              {bg ? "Топ 5" : "Top 5"}
              <ArrowRight className="w-3 h-3" />
            </button>
          </div>

          {/* Input */}
          <div className="flex items-center gap-2">
            <div className={`flex-1 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border
              ${isDark ? "bg-white/5 border-white/10" : "bg-white border-gray-200"}`}>
              <Search className={`w-3.5 h-3.5 ${isDark ? "text-gray-400" : "text-gray-500"}`} />
              <input
                type="text"
                value={foodName}
                onChange={(e) => setFoodName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleCheck()}
                placeholder="напр. сьомга с броколи"
                className={`flex-1 bg-transparent text-xs outline-none ${isDark ? "text-white placeholder-gray-500" : "text-gray-900 placeholder-gray-400"}`}
                data-testid="meal-compliance-input"
              />
            </div>
            <button
              onClick={handleCheck}
              disabled={checking || !foodName.trim()}
              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors disabled:opacity-50
                ${isDark ? "bg-indigo-500/30 text-indigo-200 hover:bg-indigo-500/40" : "bg-indigo-600 text-white hover:bg-indigo-700"}`}
              data-testid="meal-compliance-check-btn"
            >
              {checking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Провери"}
            </button>
          </div>

          {/* Result */}
          {result && (
            <div className="mt-3 space-y-2" data-testid="compliance-result">
              <div className="flex items-center gap-2 flex-wrap">
                <ScoreBadge score={result.score} status={result.status} isDark={isDark} />
                <span className={`text-xs font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
                  {result.status_bg}
                </span>
              </div>
              <div className={`text-[11px] ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                {result.rationale_bg}
              </div>

              {result.matched_contraindications?.length > 0 && (
                <div className={`rounded p-2 text-[10px] ${isDark ? "bg-red-500/10 text-red-300" : "bg-red-50 text-red-700"}`}>
                  <div className="font-bold mb-1">⛔ Противопоказания:</div>
                  {result.matched_contraindications.map((c) => (
                    <div key={`${c.phenotype}-${c.keyword}`}>· <span className="font-semibold">{c.keyword}</span> → {c.phenotype}</div>
                  ))}
                </div>
              )}

              {result.matched_avoid?.length > 0 && (
                <div className={`rounded p-2 text-[10px] ${isDark ? "bg-amber-500/10 text-amber-300" : "bg-amber-50 text-amber-700"}`}>
                  <div className="font-bold mb-1">⚠️ Избягвай:</div>
                  {result.matched_avoid.slice(0, 4).map((a) => (
                    <div key={`${a.phenotype}-${a.keyword}`}>· <span className="font-semibold">{a.keyword}</span> → {a.phenotype}</div>
                  ))}
                </div>
              )}

              {result.matched_emphasise?.length > 0 && (
                <div className={`rounded p-2 text-[10px] ${isDark ? "bg-emerald-500/10 text-emerald-300" : "bg-emerald-50 text-emerald-700"}`}>
                  <div className="font-bold mb-1">✓ Подкрепя:</div>
                  {result.matched_emphasise.slice(0, 4).map((e) => (
                    <div key={`${e.phenotype}-${e.keyword}`}>
                      · <span className="font-semibold">{e.keyword}</span> → {e.nutrient_bg} <span className="opacity-70">({e.phenotype})</span>
                    </div>
                  ))}
                </div>
              )}

              {result.conflicts?.length > 0 && (
                <div className={`rounded p-2 text-[10px] ${isDark ? "bg-purple-500/10 text-purple-300" : "bg-purple-50 text-purple-700"}`}
                  data-testid="compliance-conflicts">
                  <div className="font-bold mb-1">🔀 Конфликти между фенотипите:</div>
                  {result.conflicts.map((c) => (
                    <div key={c.keyword}>
                      · <span className="font-semibold">{c.keyword}</span>: emphasise в {c.emphasise_in.join(", ")} vs avoid в {c.avoid_in.join(", ")}
                    </div>
                  ))}
                  <div className="italic mt-1">Правило: avoid печели (безопасен подход).</div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <NextMealModal
        open={showNextMeal}
        onClose={() => setShowNextMeal(false)}
        API={API}
        isDark={isDark}
        onAddMeal={handleAddSuggestion}
      />
    </>
  );
}
